// Type import only - these don't affect bundle size
import type { OktaAuth, Tokens, TOKEN_STORAGE_NAME } from '@okta/okta-auth-js';
import { VerifyAuthResponse } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { getWeaveToken, isWeaveTokenActive, isTimeExpired, OktaConfig, SignInMethod } from '@frontend/auth-helpers';
import { sentry } from '@frontend/tracking';
import { navigateToUrl } from '../utils/navigateToUrl';
import { AuthFlow, SignOutProps, HandleCallbackProps, ClientTokens } from './AuthFlow';

// We lazy load the Okta library. One tradeoff of this is that we have to
// know the localStorage key that the library uses to store tokens
// so we can read them directly without loading the full library.
// This key is exported by the library as TOKEN_STORAGE_NAME
// We use TS trickery here to get the type without importing the actual value
// and enforce that our string constant matches it. This way if the library
// changes the key in the future, we'll get a type error here at build time.
const OKTA_TOKEN_STORAGE_NAME: typeof TOKEN_STORAGE_NAME = 'okta-token-storage';

export class OktaClient implements AuthFlow {
  private oktaClient: OktaAuth | null = null;
  private config: OktaConfig;

  constructor(config: OktaConfig) {
    this.config = config;
  }

  public isOktaLoaded(): boolean {
    return this.oktaClient !== null;
  }

  /**
   * Initializes the Okta client by lazy loading the `@okta/okta-auth-js` library.
   * We can do this ahead of time when we know we'll need it. This will speed up subsequent authentication flows using Okta.
   */
  public async initializeOkta(): Promise<void> {
    await this.getOktaClient();
  }

  private async getOktaClient(): Promise<OktaAuth> {
    if (!this.oktaClient) {
      const { OktaAuth } = await import('@okta/okta-auth-js');
      this.oktaClient = new OktaAuth(this.config);
    }
    return this.oktaClient;
  }

  public async signIn(): Promise<VerifyAuthResponse> {
    const oktaClient = await this.getOktaClient();
    return oktaClient.signInWithRedirect().then(() => {
      return {};
    });
  }

  /**
   * Instead of using the okta SDK to get the token and check expiration,
   * we read it directly from localStorage since the SDK method
   * results in a lot of extra code being bundled.
   *
   * See https://github.com/okta/okta-auth-js/blob/master/lib/oidc/TokenManager.ts
   */
  private getOktaTokensFromStorage(): Tokens {
    const oktaTokenStorage = localStorage.getItem(OKTA_TOKEN_STORAGE_NAME); // key used by okta-auth-js to store tokens
    let oktaTokens: Tokens = {};
    if (oktaTokenStorage !== null) {
      try {
        oktaTokens = JSON.parse(oktaTokenStorage);
      } catch (error) {
        // If we can't parse the token storage, return empty tokens
      }
    }
    return oktaTokens;
  }

  /**
   * The okta token determines if the user has access to the app.
   * This is only applicable if we're authenticating with okta.
   */
  isOktaTokenActive(): boolean {
    const oktaTokens = this.getOktaTokensFromStorage();
    /**
     * We need to check against the access token (8 hour expiration), not any other token
     */
    if (oktaTokens.accessToken) {
      return !isTimeExpired(oktaTokens.accessToken?.expiresAt);
    }
    return false;
  }

  getSignInMethod(): Promise<SignInMethod> {
    return Promise.resolve('okta');
  }

  async getTokens(): Promise<ClientTokens> {
    const oktaClient = await this.getOktaClient();
    const oktaTokens = oktaClient.tokenManager.getTokensSync();
    const weaveToken = getWeaveToken();
    const tokens: ClientTokens = {
      accessToken: oktaTokens.accessToken?.accessToken,
      IDToken: oktaTokens.idToken?.idToken,
      weaveToken: weaveToken,
    };
    return tokens;
  }

  async handleCallback(_?: HandleCallbackProps): Promise<ClientTokens> {
    const oktaClient = await this.getOktaClient();
    return await oktaClient.token
      .parseFromUrl()
      .then((res) => {
        const tokens = res.tokens;
        oktaClient.tokenManager.setTokens(tokens);
        return {
          accessToken: tokens.accessToken?.accessToken,
          IDToken: tokens.idToken?.idToken,
        };
      })
      .catch((err) => {
        sentry.error({
          topic: 'auth',
          team: 'users-infra',
          error: err,
          addContext: {
            name: 'auth',
            context: {
              errMessage: 'parsing token from url',
            },
          },
        });
        if (err?.errorSummary === 'The JWT expired and is no longer valid') {
          throw new Error(`${err}. Please ensure your computer clock is on the correct time and then Start Over.`);
        } else {
          throw new Error(err);
        }
      });
  }

  isUserAuthenticated(): boolean {
    const oktaTokens = this.getOktaTokensFromStorage();
    const isWeaveActive = isWeaveTokenActive();
    const isOktaActive = this.isOktaTokenActive();
    return oktaTokens?.accessToken ? isOktaActive && isWeaveActive : isWeaveActive;
  }

  /**
   * Okta sign out method will redirect the browser
   */
  public async signOut(props?: SignOutProps): Promise<void> {
    const oktaClient = await this.getOktaClient();
    const tokens = oktaClient.tokenManager.getTokensSync();
    if (tokens.idToken) {
      // this will result in a browser redirect, the promise will never resolve
      await oktaClient.signOut({
        clearTokensBeforeRedirect: true,
        idToken: tokens.idToken,
        postLogoutRedirectUri: props?.logoutRedirect ?? `${window.origin}/sign-out/callback`,
      });
    } else {
      navigateToUrl(props?.logoutRedirect ?? `${window.origin}/sign-out/callback`);
    }
  }
}
